import "./polyfills";
import "./setupApiClient";
import "./i18n";
import 'linkify-plugin-mention';
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import '@fontsource/rubik-one/index.css';
import '@fontsource/lato/300.css'; // Light
import '@fontsource/lato/400.css'; // Regular
import '@fontsource/lato/700.css'; // Bold
import { initGlobalButtonHaptic } from './utils/telegram/globalButtonHaptic';
import { createRetryFunction } from './utils/net/apiError';
import { DEFAULT_MAX_RETRIES, DEFAULT_RETRY_DELAY_MS } from './utils/net/retryFetch';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import telegramAnalytics from '@telegram-apps/analytics';
import * as Sentry from '@sentry/react';
import { TracewayGate } from './utils/traceway/TracewayGate';


// Важливо: викликати ДО render() або створення кореневого компонента.
// Skip in local/e2e so analytics never leaves the machine.
if (import.meta.env.PROD) {
  try {
    telegramAnalytics.init({
      // Отримайте токен через @DataChief_bot в Telegram
      token: 'eyJhcHBfbmFtZSI6IndlYjNpc2xhbmRzX3N1cGVyX25ld182XzAyXzIwMjYiLCJhcHBfdXJsIjoiaHR0cHM6Ly90Lm1lL3dlYjNpc2xhbmRzX2JvdCIsImFwcF9kb21haW4iOiJodHRwczovL3dlYjNpc2xhbmRzLmNvbSJ9!nZ6pxSd+axOgp1qUWxJgmPoML2BY/oiWTInPx1Dvigc=',
      // Ідентифікатор, який ви вказали при реєстрації бота
      appName: 'web3islands_super_new_6_02_2026',
    });
  } catch (e) {
    console.warn("Telegram analytics init failed", e);
    Sentry.captureException(e, {
      tags: { source: 'telegram-analytics-init' },
      level: 'warning',
    });
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry для React Query запитів (useQuery) - для запитів через кеш
      // Прямі виклики API використовують fetchWithRetryForDirectCalls
      // Не робить retry для серверних помилок (5xx) - щоб не дудосити сервер
      // УНІФІКАЦІЯ: використовує DEFAULT_MAX_RETRIES для однакової кількості спроб з прямими викликами
      retry: createRetryFunction(DEFAULT_MAX_RETRIES),
      retryDelay: () => DEFAULT_RETRY_DELAY_MS,
      // Зберігаємо кеш назавжди, щоб дані не зникали після неактивності
      gcTime: Infinity, // Колишнє cacheTime - час зберігання неактивних даних
      staleTime: 5 * 60 * 1000, // 5 хвилин - дані вважаються свіжими протягом цього часу
      refetchOnMount: false, // Не робити refetch при монтуванні (використовуємо кеш)
      refetchOnWindowFocus: false, // Не робити refetch при фокусі вікна
      refetchOnReconnect: false, // Не робити refetch при переподключенні
    },
    mutations: {
      // Retry для React Query мутацій (useMutation)
      // Прямі виклики API використовують fetchWithRetryForDirectCalls
      // Не робить retry для серверних помилок (5xx) - щоб не дудосити сервер
      // УНІФІКАЦІЯ: використовує DEFAULT_MAX_RETRIES для однакової кількості спроб з прямими викликами
      retry: createRetryFunction(DEFAULT_MAX_RETRIES),
      retryDelay: () => DEFAULT_RETRY_DELAY_MS,
    },
  },
});

// Profiling builds (tools/profiling): the UI store on window, to walk into rooms.
if (import.meta.env.VITE_SCENE_PROBE) {
  void import('./stores/useInterfaceStore').then((m) => {
    (globalThis as unknown as { __ui?: unknown }).__ui = m.default;
  });
}

// Ініціалізуємо глобальний обробник вібрації для кнопок
initGlobalButtonHaptic();


const POSTHOG_API_KEY = 'phc_Y65Pw8t1LSYrH0JU3z3Sdvr37PT0KLyVMzU8QS19f7e';
const POSTHOG_HOST = 'https://us.i.posthog.com';

const posthogOptions = {
  api_host: POSTHOG_HOST,
  defaults: '2025-11-30',
} as const

// Skip PostHog in local/e2e — must not leave localhost (guardNoProd whitelist).
if (import.meta.env.PROD && POSTHOG_API_KEY && POSTHOG_HOST) {
  posthog.init(POSTHOG_API_KEY, posthogOptions);
  window.posthog = posthog;
}

let hasCapturedGameClosed = false;
const captureGameClosed = (reason: string) => {
  if (hasCapturedGameClosed) return;
  hasCapturedGameClosed = true;
  if (import.meta.env.PROD) {
    posthog.capture('game_closed', { reason });
  }
};

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    captureGameClosed('visibility_hidden');
  }
});

window.addEventListener('pagehide', () => {
  captureGameClosed('pagehide');
});

window.addEventListener('beforeunload', () => {
  captureGameClosed('beforeunload');
});

const appTree = (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--gradient-city-hall-background)',
            color: 'var(--color-white)',
            padding: 24,
            textAlign: 'center',
            fontFamily: 'Lato, sans-serif',
            zIndex: 999999,
          }}>
            <div style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                The game crashed. Please reopen it.
              </div>
              <div style={{ fontSize: 13, opacity: 0.75, wordBreak: 'break-word' }}>
                {String(error instanceof Error ? error.message : error)}
              </div>
              <button
                onClick={() => {
                  try { resetError(); } catch { /* ignore */ }
                  try { window.location.reload(); } catch { /* ignore */ }
                }}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: `var(--gradient-gold-warm)`,
                  border: 'none',
                  borderRadius: 16,
                  color: 'var(--color-black)',
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Reload
              </button>
              <a
                href="https://t.me/web3islands_contact"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--color-white-alpha-60)',
                  fontSize: 13,
                  textDecoration: 'underline',
                }}
              >
                Contact support
              </a>
            </div>
          </div>
        )}
      >
        <TracewayGate>
          <App />
        </TracewayGate>
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  </BrowserRouter>
);

// PostHog only in prod builds. The TON wallet is not mounted here: it loads on
// first use (hooks/useTonConnect.ts).
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {import.meta.env.PROD ? (
      <PostHogProvider apiKey={POSTHOG_API_KEY} options={posthogOptions}>
        {appTree}
      </PostHogProvider>
    ) : (
      appTree
    )}
  </StrictMode>
)
